import React, { useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Layout } from '@/components/layout/Layout';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ErrorDisplay } from '@/components/common/ErrorDisplay';
import { adminApi } from '@/services/api';

interface AnalysisListItem {
  id: string;
  date: string;
  text_snippet: string;
  title?: string;
  context?: string;
  sentiment: {
    positivity: number;
    negativity: number;
    neutral: number;
  };
  metadata?: {
    source?: 'email' | 'dashboard';
    subject?: string;
  };
}

const AdminUserHistory: React.FC = () => {
  const { id: userId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const perPage = 10;

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['admin', 'user-history', userId, page],
    queryFn: async () => {
      const response = await adminApi.getUserAnalyses(userId!, { page, per_page: perPage });
      return response.data?.data;
    },
    enabled: !!userId,
    staleTime: 60 * 1000,
    retry: 2,
  });

  const getSentimentColor = (sentiment: { positivity: number; negativity: number; neutral: number }) => {
    const max = Math.max(sentiment.positivity, sentiment.negativity, sentiment.neutral);
    if (max === sentiment.positivity) return 'bg-green-100 text-green-700';
    if (max === sentiment.negativity) return 'bg-red-100 text-red-700';
    return 'bg-gray-100 text-gray-700';
  };

  const getSentimentLabel = (sentiment: { positivity: number; negativity: number; neutral: number }) => {
    const max = Math.max(sentiment.positivity, sentiment.negativity, sentiment.neutral);
    if (max === sentiment.positivity) return 'Positive';
    if (max === sentiment.negativity) return 'Negative';
    return 'Neutral';
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const userInfo = data?.user || { name: '', email: '' };
  const analyses: AnalysisListItem[] = data?.analyses || [];
  const pagination = data?.pagination;

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Back Navigation */}
        <div className="mb-4">
          <Link
            to="/admin/users"
            className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            <span className="material-symbols-outlined text-xl mr-1">arrow_back</span>
            Back to Users
          </Link>
        </div>

        {/* Admin Banner */}
        {(userInfo.name || userInfo.email) && (
          <div className="mb-6 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
            <div className="flex items-center gap-2">
              <span className="material-symbols-outlined text-amber-600">admin_panel_settings</span>
              <span className="text-amber-800 font-medium">
                Viewing history for: {userInfo.name || userInfo.email}
              </span>
              {userInfo.name && userInfo.email && (
                <span className="text-amber-600 text-sm">({userInfo.email})</span>
              )}
            </div>
            <div className="mt-2 flex gap-3">
              <Link
                to={`/admin/users/${userId}/analytics`}
                className="text-sm text-amber-700 hover:text-amber-900 underline"
              >
                View analytics dashboard
              </Link>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Analysis History</h1>
          <p className="text-sm sm:text-base text-gray-600">
            Past communication analyses for {userInfo.name || userInfo.email || 'this user'}
          </p>
        </div>

        {/* Loading */}
        {isLoading && (
          <div className="flex justify-center py-12">
            <LoadingSpinner size="lg" message="Loading analyses..." showTimeoutMessage={true} />
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="flex justify-center">
            <ErrorDisplay
              type="network"
              title="Unable to Load History"
              message="We couldn't load the analysis history for this user."
              onRetry={() => refetch()}
              onGoBack={() => navigate('/admin/users')}
              showRetry={true}
              showGoBack={true}
            />
          </div>
        )}

        {/* Empty */}
        {!isLoading && !error && analyses.length === 0 && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 sm:p-12 text-center">
            <span className="material-symbols-outlined text-gray-400 text-5xl sm:text-6xl mb-4">
              search_off
            </span>
            <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-2">No Analyses Yet</h2>
            <p className="text-sm sm:text-base text-gray-600">
              This user hasn't analysed any communications yet.
            </p>
          </div>
        )}

        {/* Analyses List */}
        {!isLoading && !error && analyses.length > 0 && (
          <>
            <div className="space-y-4">
              {analyses.map((analysis) => (
                <Link
                  key={analysis.id}
                  to={`/admin/analysis/${analysis.id}`}
                  className="block bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md hover:border-primary-300 transition-all"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getSentimentColor(analysis.sentiment)}`}>
                          {getSentimentLabel(analysis.sentiment)}
                        </span>

                        {analysis.metadata?.source === 'email' && (
                          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                            <span className="material-symbols-outlined text-sm">mail</span>
                            Email
                          </span>
                        )}

                        {analysis.context && (
                          <span className="px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                            {analysis.context}
                          </span>
                        )}

                        <span className="text-sm text-gray-500">{formatDate(analysis.date)}</span>
                      </div>

                      {analysis.metadata?.source === 'email' && analysis.metadata?.subject && (
                        <div className="flex items-center gap-2 mb-2">
                          <span className="material-symbols-outlined text-sm text-gray-400">subject</span>
                          <span className="text-sm text-gray-600 italic">{analysis.metadata.subject}</span>
                        </div>
                      )}

                      {analysis.title && (
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">{analysis.title}</h3>
                      )}

                      <p className="text-gray-600 leading-relaxed">{analysis.text_snippet}</p>
                    </div>

                    <span className="material-symbols-outlined text-gray-400 ml-4">chevron_right</span>
                  </div>
                </Link>
              ))}
            </div>

            {/* Pagination */}
            {pagination && pagination.pages > 1 && (
              <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row items-center justify-between gap-4 border-t border-gray-200 pt-4 sm:pt-6">
                <div className="text-xs sm:text-sm text-gray-600">
                  Showing page {pagination.page} of {pagination.pages} ({pagination.total} total)
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page === 1}
                    className="px-3 sm:px-4 py-2 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <span className="material-symbols-outlined text-sm">chevron_left</span>
                  </button>
                  <button
                    onClick={() => setPage(Math.min(pagination.pages, page + 1))}
                    disabled={page === pagination.pages}
                    className="px-3 sm:px-4 py-2 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <span className="material-symbols-outlined text-sm">chevron_right</span>
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
};

export default AdminUserHistory;
